import { resolveProjectBreadcrumbRoute } from "./projectBreadcrumbRoute";
import { usePublishedBreadcrumbParent } from "./publishedBreadcrumbParent";
import { deriveLogicalProjectKey } from "../../logicalProject";
import { savedBreadcrumbProjectRouteKey } from "./savedThreadBreadcrumb";
import { useSavedThreadBreadcrumb } from "./useSavedThreadBreadcrumb";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";
import type { EnvironmentId, ProjectId } from "@cmmd-forge/contracts";
import { useLocation, useParams } from "@tanstack/react-router";

import { useCmmdWorkIntakeSnapshot } from "~/cmmd/workIntakeResource";
import { useComposerDraftStore } from "~/composerDraftStore";
import { selectProjectByRef, selectSidebarThreadSummaryByRef, useStore } from "~/store";
import { resolveThreadRouteTarget, type ThreadRouteTarget } from "~/threadRoutes";
import { pluginDetailMetadata } from "../plugins/pluginMetadata";
import { isForgePluginId } from "../plugins/pluginsPage.logic";
import { SETTINGS_NAV_ITEMS } from "../settings/SettingsSidebarNav";
import { projectDisplayName } from "../projects/projectDisplayName";
import { useProjectIconResolver } from "../projects/useProjectIconResolver";
import {
  resolveStaticBreadcrumb,
  splitPathname,
  withParentCrumb,
  type BreadcrumbCrumb,
  type BreadcrumbCrumbIcon,
} from "./globalBreadcrumb.logic";
import {
  resolveBreadcrumbCrumbs,
  resolveBreadcrumbProjectRef,
  type BreadcrumbProjectRef,
} from "./globalBreadcrumbRouting";
import { useGlobalBreadcrumbStore, type PublishedBreadcrumbLabel } from "./globalBreadcrumbStore";

/**
 * The settings nav array is the single source of truth for section labels, so
 * the breadcrumb reads it rather than keeping a parallel map that could drift.
 */
const SETTINGS_LABEL_BY_PATH: ReadonlyMap<string, string> = new Map(
  SETTINGS_NAV_ITEMS.map((item) => [item.to, item.label]),
);

/**
 * Resolves the crumbs for the current route.
 *
 * Everything here is a synchronous read of state the app already holds — the
 * thread store, the draft store, the CMMD work-intake snapshot, a static plugin
 * table. The bar must never be the reason a request is made, and it must never
 * render empty, so every branch starts from the static answer and only refines
 * it once a real name is in hand. The choosing itself lives in
 * `globalBreadcrumbRouting`.
 */
export function useGlobalBreadcrumbCrumbs(): readonly BreadcrumbCrumb[] {
  const pathname = useLocation({ select: (location) => location.pathname });
  // No `select` here on purpose. This router does not enable structural
  // sharing, so a `select` that builds a fresh object every render never
  // compares equal, re-renders forever, and takes the whole app down with it —
  // the bar is mounted above every route, so the blank screen is total. The
  // unselected params object is the match's own, and its identity is stable.
  const routeParams = useParams({ strict: false }) as Partial<
    Record<"environmentId" | "threadId" | "draftId", string>
  >;

  const publishedLabel = usePublishedLabel(pathname);
  const publishedParent = usePublishedBreadcrumbParent(pathname);
  const routeTarget = resolveThreadRouteTarget(routeParams);
  const thread = useThreadCrumbSource(routeTarget);
  const draftProjectRef = useDraftProjectRef(routeTarget);

  const { savedIdentity, savedProjectRef } = useSavedThreadBreadcrumb(routeTarget, draftProjectRef);
  const projectRef = resolveBreadcrumbProjectRef({
    draftProjectRef,
    pathname,
    threadProjectRef: thread.projectRef ?? savedProjectRef,
  });
  const liveProject = useLiveProjectMetadata(projectRef);
  const projectName = liveProject.name ?? savedIdentity?.projectName ?? null;
  const projectRouteKey = savedBreadcrumbProjectRouteKey(savedIdentity, liveProject.routeKey);
  const resolveProjectIcon = useProjectIconResolver();
  const cmmdSnapshot = useCmmdWorkIntakeSnapshot();

  const cmmdProject = resolveCmmdProject(pathname, cmmdSnapshot.projects);

  const crumbs = resolveBreadcrumbCrumbs({
    cmmdProjectIcon: cmmdProjectBreadcrumbIcon(cmmdProject),
    // The breadcrumb is Forge chrome, so it shows the Forge name. The board
    // name stays on the board.
    cmmdProjectTitle: projectDisplayName(cmmdProject),
    isThreadRoute: routeTarget !== null,
    pathname,
    projectIcon: resolveRuntimeProjectIcon({
      canonicalIdentity: resolveProjectIcon(projectRef?.environmentId, projectRef?.projectId),
      cwd: liveProject.cwd,
      environmentId: projectRef?.environmentId ?? null,
      projectName,
    }),
    projectName,
    projectRouteKey,
    projectRef,
    publishedLabel,
    staticCrumbs: resolveStaticCrumbs(pathname),
    threadTitle: thread.title ?? savedIdentity?.threadTitle ?? null,
  });
  return withParentCrumb(crumbs, publishedParent);
}

function useDraftProjectRef(routeTarget: ThreadRouteTarget | null): BreadcrumbProjectRef | null {
  const session = useComposerDraftStore((store) =>
    routeTarget?.kind === "draft" ? store.getDraftSession(routeTarget.draftId) : null,
  );
  return toProjectRef(session);
}

function useLiveProjectMetadata(projectRef: BreadcrumbProjectRef | null) {
  const name = useStore(
    (state) => selectProjectByRef(state, toScopedProjectRef(projectRef))?.name ?? null,
  );
  const cwd = useStore(
    (state) => selectProjectByRef(state, toScopedProjectRef(projectRef))?.cwd ?? null,
  );
  const routeKey = useStore((state) =>
    projectRouteKeyFor(selectProjectByRef(state, toScopedProjectRef(projectRef))),
  );
  return { name, cwd, routeKey };
}

/**
 * Two primitive reads rather than one object selector.
 *
 * `selectPublishedLabelForPath` builds a fresh object, and zustand compares with
 * `Object.is` — selecting it directly is the unstable-selector trap that blanked
 * the whole app once already, because this bar is mounted above every route.
 */
function usePublishedLabel(pathname: string): PublishedBreadcrumbLabel | null {
  const key = useGlobalBreadcrumbStore((store) => (store.pathname === pathname ? store.key : null));
  const label = useGlobalBreadcrumbStore((store) =>
    store.pathname === pathname ? store.label : null,
  );
  return key !== null && label !== null ? { key, label } : null;
}

/**
 * A saved thread's title and project, read as primitives.
 *
 * `selectSidebarThreadSummaryByRef` allocates a fresh summary whenever a thread
 * has no cached sidebar entry — precisely the window a cold thread route renders
 * through — so selecting the summary itself never compares equal. Selecting
 * `string | null` makes the comparison honest.
 */
function useThreadCrumbSource(routeTarget: ThreadRouteTarget | null): {
  readonly title: string | null;
  readonly projectRef: BreadcrumbProjectRef | null;
} {
  const isServerThread = routeTarget?.kind === "server";
  const title = useStore((state) =>
    isServerThread && routeTarget !== null && routeTarget.kind === "server"
      ? (selectSidebarThreadSummaryByRef(state, routeTarget.threadRef)?.title ?? null)
      : null,
  );
  const projectId = useStore((state) =>
    isServerThread && routeTarget !== null && routeTarget.kind === "server"
      ? (selectSidebarThreadSummaryByRef(state, routeTarget.threadRef)?.projectId ?? null)
      : null,
  );

  if (routeTarget === null || routeTarget.kind !== "server" || projectId === null) {
    return { projectRef: null, title };
  }
  return {
    projectRef: { environmentId: routeTarget.threadRef.environmentId, projectId },
    title,
  };
}

/**
 * Plugin titles come from a static table, so they are folded into the static
 * answer rather than treated as a late-arriving name.
 */
function resolveStaticCrumbs(pathname: string): readonly BreadcrumbCrumb[] {
  const crumbs = resolveStaticBreadcrumb(pathname, SETTINGS_LABEL_BY_PATH);
  const segments = splitPathname(pathname);
  const pluginId = segments[0] === "plugins" ? segments[1] : undefined;
  if (pluginId === undefined || !isForgePluginId(pluginId)) {
    return crumbs;
  }

  const leaf = crumbs[crumbs.length - 1];
  if (leaf === undefined) {
    return crumbs;
  }
  return [...crumbs.slice(0, -1), { ...leaf, label: pluginDetailMetadata(pluginId).title }];
}

interface CmmdProjectIdentity {
  readonly id: number | string;
  readonly title: string;
  readonly icon?: string | null;
  readonly iconUrl?: string | null;
}

function resolveCmmdProject(
  pathname: string,
  projects: readonly CmmdProjectIdentity[],
): CmmdProjectIdentity | null {
  const cmmdProjectId = resolveProjectBreadcrumbRoute(pathname)?.boardId;
  return projects.find((candidate) => String(candidate.id) === cmmdProjectId) ?? null;
}

function toProjectRef(
  source: { readonly environmentId: string; readonly projectId: string } | null | undefined,
): BreadcrumbProjectRef | null {
  if (source === null || source === undefined) {
    return null;
  }
  return { environmentId: source.environmentId, projectId: source.projectId };
}

function toScopedProjectRef(ref: BreadcrumbProjectRef | null) {
  return ref === null
    ? null
    : scopeProjectRef(ref.environmentId as EnvironmentId, ref.projectId as ProjectId);
}

/**
 * The mark for a project reached through a Forge runtime.
 *
 * Returns null until the project has a name, so a cold route renders the
 * static crumb rather than an anonymous letter tile. `canonicalIdentity` stays
 * `undefined` for a local checkout, which is what sends `ProjectIcon` down the
 * favicon branch.
 */
function resolveRuntimeProjectIcon(input: {
  readonly canonicalIdentity:
    | { readonly iconUrl: string | null; readonly icon: string | null }
    | undefined;
  readonly cwd: string | null;
  readonly environmentId: string | null;
  readonly projectName: string | null;
}): BreadcrumbCrumbIcon | null {
  if (input.projectName === null || input.environmentId === null) {
    return null;
  }
  return {
    name: input.projectName,
    ...(input.canonicalIdentity === undefined
      ? {}
      : { canonicalIdentity: input.canonicalIdentity }),
    environmentId: input.environmentId,
    cwd: input.cwd ?? "",
  };
}

function cmmdProjectBreadcrumbIcon(cmmdProject: ReturnType<typeof resolveCmmdProject>) {
  if (cmmdProject === null) return null;
  return {
    canonicalIdentity: { icon: cmmdProject.icon ?? null, iconUrl: cmmdProject.iconUrl ?? null },
    name: projectDisplayName(cmmdProject) ?? cmmdProject.title,
  };
}

function projectRouteKeyFor(
  project: Parameters<typeof deriveLogicalProjectKey>[0] | undefined,
): string | null {
  return project ? deriveLogicalProjectKey(project) : null;
}
